"use client";

import * as React from "react";

import { Button } from "@components/ui/button";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@components/ui/command";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Separator } from "@components/ui/separator";
import { Input } from "../ui/input";
import { CheckCheck, X } from "lucide-react";
import { CustomSelectProps, CustomSelectItemProps } from "./CommonTypes";
import Confirmation from "./ConfirmDialog";

const CustomSelect: React.FC<CustomSelectProps> = React.memo(
  ({
    defaultValue,
    data,
    onSelect,
    placeholder = "",
    multiSelect = false,
    onClear,
    sorted = false,
    resetTrigger = 0,
    clearOnSelect = false,
    disabled = false,
    size = "default",
    className = "",
    onAddNew,
  }) => {
    if (sorted) {
      data.sort((a, b) => a.label.localeCompare(b.label));
    }
    const sizeStyles = React.useMemo(() => {
      switch (size) {
        case "sm":
          return {
            button: {
              height: "1.75rem",
              minHeight: "1.75rem",
              padding: "0.25rem 0.5rem",
              fontSize: "0.75rem",
            } as React.CSSProperties,
            icon: {
              color: "#ef4444",
            } as React.CSSProperties,
          };
        case "lg":
          return {
            button: {
              height: "2.75rem",
              minHeight: "2.75rem",
              padding: "0.5rem 1rem",
              fontSize: "1rem",
            } as React.CSSProperties,
            icon: {
              color: "#3b82f6",
            } as React.CSSProperties,
          };
        case "default":
        default:
          return {
            button: {} as React.CSSProperties,
            icon: {} as React.CSSProperties,
          };
      }
    }, [size]);

    const listRef = React.useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = React.useState<number>(1);
    const searchRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const [showAddConfirm, setShowAddConfirm] = React.useState(false);
    const [pendingAddValue, setPendingAddValue] = React.useState("");
    const [mainValue, setMainValue] = React.useState<string[]>(
      multiSelect ? (defaultValue as string[]) : [defaultValue as string],
    );
    const [searchItems, setSearchItems] = React.useState<any>(data);

    const [initvalue, setInitValue] = React.useState<string>("");

    React.useEffect(() => {
      if (data) {
        const newVal = displayValue(
          multiSelect ? (defaultValue as string[]) : [defaultValue as string],
        );
        setInitValue(newVal);
        setMainValue(
          multiSelect ? (defaultValue as string[]) : [defaultValue as string],
        );
        setSearchItems(data);
      }
    }, [data]);

    React.useEffect(() => {
      if (resetTrigger > 0) {
        setMainValue(multiSelect ? [] : [""]);
        setInitValue("");
      }
    }, [resetTrigger]);

    React.useEffect(() => {
      setCurrentIndex(1);
    }, [searchItems]);

    function valueChanged(e: string) {
      if (e == "") {
        onClear && onClear();
      }
      let newValue: string[] = mainValue;
      if (mainValue.includes(e)) {
        newValue = mainValue.filter((item) => item != e);
      } else {
        if (multiSelect) {
          if (e == "") {
            newValue = [];
          } else {
            newValue.push(e);
          }
        } else {
          if (e == "") {
            newValue = [];
          } else {
            newValue = [e];
          }
        }
      }
      setMainValue(newValue);
      if (onSelect) {
        if (multiSelect) {
          onSelect(newValue);
        } else {
          if (newValue?.length > 0) {
            onSelect(newValue[0]);
          } else {
            const selectedLabels: string = displayValue(newValue);
            setInitValue(selectedLabels);
            onSelect("");
          }
        }
      }

      const selectedLabels: string = displayValue(newValue);
      setInitValue(selectedLabels);
      if (clearOnSelect) {
        setMainValue(multiSelect ? [] : [""]);
        setInitValue("");
      }
    }

    function displayValue(newValue: string[]) {
      let selectedLabels: string[] = [];
      if (data) {
        if (multiSelect) {
          data?.forEach((item: CustomSelectItemProps) => {
            if (newValue?.includes(item?.value)) {
              selectedLabels?.push(item?.label);
            }
          });
        } else {
          data?.forEach((item: CustomSelectItemProps) => {
            if (newValue[0] == item?.value) {
              selectedLabels = [item?.label];
            }
          });
        }
      }
      return selectedLabels?.join(", ");
    }

  React.useEffect(() => {
    const normalizedValue = multiSelect
      ? Array.isArray(defaultValue)
        ? (defaultValue as string[])
        : []
      : [typeof defaultValue === "string" ? defaultValue : ""];
    setMainValue(normalizedValue);
    const newVal = displayValue(normalizedValue);
    setInitValue(newVal);
  }, [JSON.stringify(defaultValue), multiSelect]);

    const onListScroll = () => {
      const el = listRef.current;
      if (!el) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setCurrentIndex((prev) => prev + 1);
      }
    };

    function searchSelect(e: string) {
      setSearchItems(
        data.filter((item: CustomSelectItemProps) =>
          item.label.toLowerCase().includes(e.toLowerCase()),
        ),
      );
    }

    async function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key !== "Enter") return;

      if (!onAddNew) return;

      const searchValue = (e.currentTarget.value || "").trim();
      if (!searchValue) return;

      const filteredItems = data.filter((item: CustomSelectItemProps) =>
        item.label.toLowerCase().includes(searchValue.toLowerCase()),
      );

      if (filteredItems.length === 0) {
        e.preventDefault();
        e.stopPropagation();
        setPendingAddValue(searchValue);
        setShowAddConfirm(true);
      }
    }

    async function confirmAddNewOption() {
      if (!onAddNew) {
        setShowAddConfirm(false);
        return;
      }

      const searchValue = pendingAddValue.trim();
      if (!searchValue) {
        setShowAddConfirm(false);
        return;
      }

      try {
        await onAddNew(searchValue);
        const createdItem: CustomSelectItemProps = {
          label: searchValue,
          value: searchValue,
        };

        if (createdItem && createdItem.label && createdItem.value) {
          setSearchItems((prev: CustomSelectItemProps[]) => {
            const baseItems = Array.isArray(prev) ? prev : [];
            const exists = baseItems.some(
              (item) =>
                item.value.toLowerCase() === createdItem.value.toLowerCase() ||
                item.label.toLowerCase() === createdItem.label.toLowerCase(),
            );
            return exists ? baseItems : [...baseItems, createdItem];
          });
        }
      } catch (error) {
        // Keep failures isolated to the add-new flow so the select stays usable.
        console.error("Failed to add new option in CustomSelect", error);
      } finally {
        setShowAddConfirm(false);
        setPendingAddValue("");
      }
    }

    function selectAll() {
      if (data) {
        let searchValue = "";
        const sref = searchRef.current;
        if (sref) {
          searchValue = sref.value.trim().toLocaleLowerCase();
        }
        const tempValue: string[] = [];
        data.forEach((item: CustomSelectItemProps) => {
          if (item.label.toLowerCase().includes(searchValue)) {
            tempValue.push(item.value);
          }
        });
        setMainValue(tempValue);
        if (onSelect) {
          onSelect(tempValue);
        }
        const selectedLabels: string = displayValue(tempValue);
        setInitValue(selectedLabels);
      }
    }

    function deselectAll() {
      setMainValue([]);
      if (onSelect) {
        onSelect([]);
      }
      setInitValue("");
    }

    return (
      <>
        <Popover open={open} onOpenChange={setOpen} modal={false}>
          <div
            className={
              className +
              " relative " +
              (initvalue.length > 0 ? "pe-8 border-r" : "")
            }
          >
            <PopoverTrigger asChild className="relative">
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={`w-full justify-between overflow-hidden ${className}`}
                onClick={() => setSearchItems(data)}
                title={initvalue}
                disabled={disabled}
                style={sizeStyles.button}
              >
                {initvalue != "" ? (
                  <span className="truncate">{initvalue}</span>
                ) : (
                  <div className="opacity-60">{placeholder}</div>
                )}
              </Button>
            </PopoverTrigger>
            {initvalue.length > 0 ? (
              <X
                size={size === "sm" ? 14 : size === "lg" ? 18 : 16}
                onClick={() => valueChanged("")}
                className="absolute top-2/4 right-2 -translate-y-2/4 cursor-pointer"
                style={sizeStyles.icon}
              />
            ) : (
              ""
            )}
          </div>
          <PopoverContent
            className="w-full max-w-100 pointer-events-auto "
            align="start"
            onWheel={(e) => e.stopPropagation()}
          >
            <Command className="w-full">
              <Input
                onKeyUp={(e: any) => searchSelect(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search..."
                className="h-9 mb-2"
                ref={searchRef}
              />
              {multiSelect && (
                <div className="flex gap-2 justify-center p-1">
                  <Button
                    variant="outline"
                    className="flex-1 hover:scale-110"
                    onClick={selectAll}
                    disabled={mainValue?.length == data.length}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 hover:scale-110"
                    onClick={deselectAll}
                    disabled={mainValue?.length == 0}
                  >
                    Deselect All
                  </Button>
                  {
                    <Button
                      variant="outline"
                      className="flex-1 hover:scale-110"
                      onClick={() => setOpen(false)}
                    >
                      Apply
                    </Button>
                  }
                </div>
              )}
              <Separator className="my-1" />
              <CommandEmpty>No data found</CommandEmpty>
              <CommandGroup>
                <CommandList ref={listRef} onScroll={() => onListScroll()}>
                  {searchItems
                    ? searchItems
                        .slice(0, currentIndex * 20)
                        .map((framework: any, index: number) => {
                          if (
                            framework &&
                            framework.value &&
                            framework.label &&
                            (framework.value || "").toString().trim() != "" &&
                            (framework.label || "").toString().trim() != ""
                          ) {
                            return (
                              <CommandItem
                                key={framework.value + "_" + index}
                                value={framework.value}
                                onSelect={(currentValue) => {
                                  valueChanged(currentValue);
                                  if (!multiSelect) {
                                    setOpen(false);
                                  }
                                }}
                              >
                                <CheckCheck
                                  size={
                                    size === "sm" ? 14 : size === "lg" ? 18 : 16
                                  }
                                  className={
                                    "me-2 " +
                                    (mainValue?.includes(framework.value)
                                      ? "opacity-100"
                                      : "opacity-0")
                                  }
                                />
                                {framework.label}
                              </CommandItem>
                            );
                          }
                        })
                    : ""}
                </CommandList>
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
        <Confirmation
          title="Add New Option"
          text={`"${pendingAddValue}" was not found. Do you want to add it as a new option?`}
          confirmAction={confirmAddNewOption}
          rejectAction={() => {
            setShowAddConfirm(false);
            setPendingAddValue("");
          }}
          confirmText="Add"
          rejectText="Cancel"
          show={showAddConfirm}
          closeDialog={() => {
            setShowAddConfirm(false);
            setPendingAddValue("");
          }}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    //Comparing data length for rerender
    return (
      prevProps.data.length == nextProps.data.length &&
      prevProps.defaultValue == nextProps.defaultValue &&
      prevProps.placeholder == nextProps.placeholder &&
      prevProps.size == nextProps.size &&
      JSON.stringify(prevProps.data) == JSON.stringify(nextProps.data)
    );
  },
);

CustomSelect.displayName = "CustomSelect";
export default CustomSelect;
